import { useState, useEffect } from 'react';
import {
  View,
  Text,
  TextInput,
  Pressable,
  ScrollView,
  Switch,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import {
  ChevronLeft,
  Plus,
  TrendingUp,
  Eye,
  BadgeCheck,
  Calendar,
  DollarSign,
  Building,
  X,
  Trash2,
} from 'lucide-react-native';
import Animated, { FadeInDown, FadeInUp } from 'react-native-reanimated';
import { useSystemThemeColors } from '@/lib/useThemeColors';
import { useProfileStore } from '@/lib/state/profile-store';
import { FundraisingRound } from '@/lib/state/inverio-store';
import { isWeb } from '@/lib/platform';
import AnimatedPressable from '@/components/AnimatedPressable';
import { handleError, reportError } from '@/lib/errors';
import { useAlert } from '@/components/AlertDialog';
import { useAuth, supabase, isSupabaseConfigured } from '@/lib/supabase';
import { buildEssentialsUpdate } from '@/lib/supabase/profile-mappers';
import { useMyStartup } from '@/lib/startup/use-my-startup';
import { persistEssentials } from '@/lib/startup/persist-essentials';
import { loadFundraisingRounds, saveFundraisingRounds } from '@/lib/startup/fundraising-rounds';
import { KeyboardAwareBody } from '@/components/modal/KeyboardAwareBody';

const STAGES = [
  { value: 'pre-seed', label: 'Pre-Seed' },
  { value: 'seed', label: 'Seed' },
  { value: 'series-a', label: 'Series A' },
  { value: 'series-b', label: 'Series B' },
  { value: 'other', label: 'Other' },
] as const;

/** Pure — no closure over component state. Hoisted to module scope so it's
 * not recreated every render. */
function formatCurrency(value: string): string {
  const num = value.replace(/[^0-9]/g, '');
  if (!num) return '';
  return Number(num).toLocaleString();
}

/** Static content — doesn't depend on component state/props. Hoisted to
 * module scope so it's not recreated every render. */
const benefits = [
  { icon: Eye, text: 'Increased visibility in investor searches' },
  { icon: BadgeCheck, text: '"Actively Raising" badge on profile' },
  { icon: TrendingUp, text: 'Priority in matched investor feeds' },
];

export default function FundraisingStatusScreen() {
  const router = useRouter();
  const colors = useSystemThemeColors();
  const { user } = useAuth();
  const { error: showError } = useAlert();
  const { startupId } = useMyStartup();

  const startupProfile = useProfileStore((s) => s.startupProfile);
  const setStartupProfile = useProfileStore((s) => s.setStartupProfile);

  const [isRaising, setIsRaising] = useState(startupProfile?.isActivelyRaising ?? false);
  const [rounds, setRounds] = useState<FundraisingRound[]>(
    startupProfile?.fundraisingHistory ?? []
  );
  const [showAddForm, setShowAddForm] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Hydrate round history from the DB — the store seeds the initial paint (and
  // survives a same-device reload via AsyncStorage), but `fundraising_rounds`
  // is the durable, cross-device source of truth. Resolving via `startupId`
  // (membership seam) is what makes this work for create-path founders whose
  // `startup_profiles.user_id` is NULL.
  useEffect(() => {
    if (!isSupabaseConfigured() || !startupId) return;
    let cancelled = false;
    (async () => {
      try {
        const dbRounds = await loadFundraisingRounds(supabase, startupId);
        if (cancelled) return;
        setRounds(dbRounds);
        const current = useProfileStore.getState().startupProfile;
        if (current) setStartupProfile({ ...current, fundraisingHistory: dbRounds });
      } catch (err) {
        reportError(err, { context: 'FundraisingStatus.loadRounds', level: 'warning' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [startupId, setStartupProfile]);

  // Add round form state
  const [newStage, setNewStage] = useState<FundraisingRound['stage']>('seed');
  const [newAmount, setNewAmount] = useState('');
  const [newDate, setNewDate] = useState('');
  const [newLeadInvestor, setNewLeadInvestor] = useState('');

  const handleAddRound = () => {
    if (!newAmount) return;

    const round: FundraisingRound = {
      id: `round-${Date.now()}`,
      stage: newStage,
      amount: Number(newAmount.replace(/[^0-9]/g, '')),
      date: newDate || new Date().toISOString().slice(0, 7),
      leadInvestor: newLeadInvestor || undefined,
      verified: false,
    };

    setRounds([...rounds, round]);
    setShowAddForm(false);
    resetForm();
  };

  const handleRemoveRound = (id: string) => {
    setRounds(rounds.filter((r) => r.id !== id));
  };

  const resetForm = () => {
    setNewStage('seed');
    setNewAmount('');
    setNewDate('');
    setNewLeadInvestor('');
  };

  const handleSave = async () => {
    if (isSaving) return;

    // Optimistic store update for instant paint; the DB writes below are the
    // durable source of truth and their failure blocks navigation.
    if (startupProfile) {
      setStartupProfile({
        ...startupProfile,
        isActivelyRaising: isRaising,
        fundraisingHistory: rounds,
      });
    }

    if (isSupabaseConfigured() && user) {
      setIsSaving(true);
      try {
        // 1) Actively-raising flag → startup_profiles, via the membership seam.
        //    Targeting by user_id used to silently miss every create-path
        //    founder (user_id NULL); `persistEssentials` resolves via
        //    team_members and asserts rows-affected. `buildEssentialsUpdate`
        //    keeps the legacy is_actively_raising boolean in sync and omits
        //    anything unset — it never re-introduces a stage/funding_target
        //    default here.
        const update = buildEssentialsUpdate({
          raisingStatus: isRaising ? 'raising' : 'not_raising',
        });
        await persistEssentials(supabase, update);

        // 2) Round history → the fundraising_rounds table (durable, cross-
        //    device). Reconciles inserts/deletes, then reloads so local state
        //    holds real row ids (new rounds entered with client-only ids).
        if (startupId) {
          await saveFundraisingRounds(supabase, startupId, rounds);
          const fresh = await loadFundraisingRounds(supabase, startupId);
          setRounds(fresh);
          const current = useProfileStore.getState().startupProfile;
          if (current) setStartupProfile({ ...current, fundraisingHistory: fresh });
        }
      } catch (err) {
        setIsSaving(false);
        const { message, isCancelled } = handleError(err, {
          context: 'FundraisingStatus.handleSave',
        });
        if (!isCancelled) showError('Could not save fundraising details', message);
        return;
      }
      setIsSaving(false);
    }

    router.back();
  };

  return (
    <View className="flex-1" style={{ backgroundColor: colors.surface.secondary }}>
      <SafeAreaView edges={isWeb ? [] : ['top', 'bottom']} className="flex-1">
        <KeyboardAwareBody
          className="flex-1"
          contentContainerStyle={{
            maxWidth: 600,
            width: '100%',
            alignSelf: 'center',
            paddingHorizontal: 20,
            paddingBottom: 32,
          }}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        >
          {/* Header */}
          <Animated.View
            entering={FadeInDown.delay(100).duration(500)}
            className="pt-4 pb-4"
          >
            <View className="flex-row items-center justify-between">
              <Pressable
                onPress={() => router.back()}
                className="w-10 h-10 rounded-full items-center justify-center"
                style={{ backgroundColor: colors.surface.primary }}
              >
                <ChevronLeft size={24} color={colors.text.primary} />
              </Pressable>
              <AnimatedPressable
                onPress={handleSave}
                disabled={isSaving}
                className="px-5 py-2.5 rounded-full"
                style={{ backgroundColor: colors.primary[600], opacity: isSaving ? 0.6 : 1 }}
              >
                <Text style={{ color: colors.text.inverse }} className="font-semibold text-sm">
                  {isSaving ? 'Saving…' : 'Save'}
                </Text>
              </AnimatedPressable>
            </View>
          </Animated.View>

          {/* Title */}
          <Animated.View
            entering={FadeInDown.delay(200).duration(500)}
            className="pb-6"
          >
            <Text style={{ color: colors.text.primary }} className="text-2xl font-bold">
              Fundraising Status
            </Text>
            <Text style={{ color: colors.text.secondary }} className="text-base mt-1">
              Let investors know if you're raising
            </Text>
          </Animated.View>

          <Animated.View entering={FadeInUp.delay(300).duration(500)} className="gap-6">
            {/* Raising Toggle */}
            <View
              className="p-4 rounded-2xl"
              style={{ backgroundColor: colors.surface.primary }}
            >
              <View className="flex-row items-center justify-between mb-4">
                <View className="flex-1">
                  <Text style={{ color: colors.text.primary }} className="text-lg font-semibold">
                    Actively Raising
                  </Text>
                  <Text style={{ color: colors.text.secondary }} className="text-sm mt-1">
                    Toggle on when you're seeking investment
                  </Text>
                </View>
                <Switch
                  value={isRaising}
                  onValueChange={setIsRaising}
                  trackColor={{ false: colors.border.medium, true: colors.primary[500] }}
                  thumbColor={colors.surface.primary}
                />
              </View>

              {isRaising && (
                <View
                  className="pt-4 gap-3"
                  style={{ borderTopWidth: 1, borderTopColor: colors.border.light }}
                >
                  <Text style={{ color: colors.text.tertiary }} className="text-xs font-medium uppercase">
                    Benefits when raising
                  </Text>
                  {benefits.map((benefit) => (
                    <View key={`benefit-${benefit.text}`} className="flex-row items-center gap-3">
                      <View
                        className="w-8 h-8 rounded-lg items-center justify-center"
                        style={{ backgroundColor: colors.primary[50] }}
                      >
                        <benefit.icon size={16} color={colors.primary[600]} />
                      </View>
                      <Text style={{ color: colors.text.secondary }} className="text-sm flex-1">
                        {benefit.text}
                      </Text>
                    </View>
                  ))}
                </View>
              )}
            </View>

            {/* Fundraising History */}
            <View>
              <View className="flex-row items-center justify-between mb-3">
                <Text style={{ color: colors.text.primary }} className="text-lg font-semibold">
                  Fundraising History
                </Text>
                {!showAddForm && (
                  <AnimatedPressable
                    onPress={() => setShowAddForm(true)}
                    className="flex-row items-center gap-1 px-3 py-2 rounded-full"
                    style={{ backgroundColor: colors.primary[50] }}
                  >
                    <Plus size={16} color={colors.primary[600]} />
                    <Text style={{ color: colors.primary[600] }} className="text-sm font-medium">
                      Add Round
                    </Text>
                  </AnimatedPressable>
                )}
              </View>

              {/* Inline Add Round Form */}
              {showAddForm && (
                <View
                  className="p-4 rounded-2xl mb-4"
                  style={{
                    backgroundColor: colors.surface.primary,
                    borderWidth: 1,
                    borderColor: colors.primary[200],
                  }}
                >
                  <View className="flex-row items-center justify-between mb-4">
                    <Text style={{ color: colors.text.primary }} className="font-semibold">
                      Add Fundraising Round
                    </Text>
                    <Pressable onPress={() => { setShowAddForm(false); resetForm(); }}>
                      <X size={20} color={colors.text.tertiary} />
                    </Pressable>
                  </View>

                  {/* Stage Selector */}
                  <View className="mb-4">
                    <Text
                      style={{ color: colors.text.secondary }}
                      className="text-sm mb-2 font-medium"
                    >
                      Stage
                    </Text>
                    <View className="flex-row flex-wrap gap-2">
                      {STAGES.map((stage) => (
                        <Pressable
                          key={stage.value}
                          onPress={() => setNewStage(stage.value)}
                          className="px-4 py-2 rounded-full"
                          style={{
                            backgroundColor:
                              newStage === stage.value
                                ? colors.primary[600]
                                : colors.surface.secondary,
                            borderWidth: 1,
                            borderColor:
                              newStage === stage.value
                                ? colors.primary[600]
                                : colors.border.light,
                          }}
                        >
                          <Text
                            style={{
                              color:
                                newStage === stage.value
                                  ? colors.text.inverse
                                  : colors.text.primary,
                            }}
                            className="text-sm font-medium"
                          >
                            {stage.label}
                          </Text>
                        </Pressable>
                      ))}
                    </View>
                  </View>

                  {/* Amount */}
                  <View className="mb-4">
                    <Text
                      style={{ color: colors.text.secondary }}
                      className="text-sm mb-2 font-medium"
                    >
                      Amount Raised *
                    </Text>
                    <View
                      className="flex-row items-center rounded-xl px-4"
                      style={{
                        backgroundColor: colors.surface.secondary,
                        borderWidth: 1,
                        borderColor: colors.border.light,
                      }}
                    >
                      <DollarSign size={20} color={colors.text.tertiary} />
                      <TextInput
                        style={{
                          flex: 1,
                          paddingVertical: 14,
                          paddingLeft: 8,
                          color: colors.text.primary,
                          fontSize: 16,
                        }}
                        placeholder="e.g., 500,000"
                        placeholderTextColor={colors.text.tertiary}
                        value={newAmount}
                        onChangeText={(text) => setNewAmount(formatCurrency(text))}
                        keyboardType="number-pad"
                      />
                    </View>
                  </View>

                  {/* Date */}
                  <View className="mb-4">
                    <Text
                      style={{ color: colors.text.secondary }}
                      className="text-sm mb-2 font-medium"
                    >
                      Date (Month/Year)
                    </Text>
                    <View
                      className="flex-row items-center rounded-xl px-4"
                      style={{
                        backgroundColor: colors.surface.secondary,
                        borderWidth: 1,
                        borderColor: colors.border.light,
                      }}
                    >
                      <Calendar size={20} color={colors.text.tertiary} />
                      <TextInput
                        style={{
                          flex: 1,
                          paddingVertical: 14,
                          paddingLeft: 8,
                          color: colors.text.primary,
                          fontSize: 16,
                        }}
                        placeholder="e.g., 2024-06"
                        placeholderTextColor={colors.text.tertiary}
                        value={newDate}
                        onChangeText={setNewDate}
                      />
                    </View>
                  </View>

                  {/* Lead Investor */}
                  <View className="mb-4">
                    <Text
                      style={{ color: colors.text.secondary }}
                      className="text-sm mb-2 font-medium"
                    >
                      Lead Investor (Optional)
                    </Text>
                    <View
                      className="flex-row items-center rounded-xl px-4"
                      style={{
                        backgroundColor: colors.surface.secondary,
                        borderWidth: 1,
                        borderColor: colors.border.light,
                      }}
                    >
                      <Building size={20} color={colors.text.tertiary} />
                      <TextInput
                        style={{
                          flex: 1,
                          paddingVertical: 14,
                          paddingLeft: 8,
                          color: colors.text.primary,
                          fontSize: 16,
                        }}
                        placeholder="e.g., Sequoia Capital"
                        placeholderTextColor={colors.text.tertiary}
                        value={newLeadInvestor}
                        onChangeText={setNewLeadInvestor}
                      />
                    </View>
                  </View>

                  {/* Add Button */}
                  <AnimatedPressable
                    onPress={handleAddRound}
                    disabled={!newAmount}
                    className="rounded-xl py-3.5 flex-row items-center justify-center gap-2"
                    style={{
                      backgroundColor: newAmount ? colors.primary[600] : colors.border.medium,
                    }}
                  >
                    <Plus size={18} color={colors.text.inverse} />
                    <Text style={{ color: colors.text.inverse }} className="font-semibold text-sm">
                      Add Round
                    </Text>
                  </AnimatedPressable>
                </View>
              )}

              {rounds.length === 0 && !showAddForm ? (
                <View
                  className="p-6 rounded-2xl items-center"
                  style={{ backgroundColor: colors.surface.primary }}
                >
                  <DollarSign size={32} color={colors.text.tertiary} />
                  <Text
                    style={{ color: colors.text.secondary }}
                    className="text-sm text-center mt-3"
                  >
                    No fundraising rounds added yet.{'\n'}Add past rounds to build credibility.
                  </Text>
                </View>
              ) : (
                <View className="gap-3">
                  {rounds.map((round) => (
                    <View
                      key={round.id}
                      className="p-4 rounded-xl flex-row items-center"
                      style={{ backgroundColor: colors.surface.primary }}
                    >
                      <View
                        className="w-10 h-10 rounded-xl items-center justify-center mr-3"
                        style={{ backgroundColor: colors.primary[50] }}
                      >
                        <DollarSign size={20} color={colors.primary[600]} />
                      </View>
                      <View className="flex-1">
                        <Text style={{ color: colors.text.primary }} className="font-semibold">
                          {STAGES.find((s) => s.value === round.stage)?.label} Round
                        </Text>
                        <Text style={{ color: colors.text.secondary }} className="text-sm">
                          ${round.amount.toLocaleString()} • {round.date}
                        </Text>
                        {round.leadInvestor && (
                          <Text style={{ color: colors.text.tertiary }} className="text-xs mt-1">
                            Lead: {round.leadInvestor}
                          </Text>
                        )}
                      </View>
                      {round.verified && (
                        <View
                          className="px-2 py-1 rounded-full mr-2"
                          style={{ backgroundColor: colors.traffic.greenLight }}
                        >
                          <Text style={{ color: colors.traffic.green }} className="text-xs font-medium">
                            Verified
                          </Text>
                        </View>
                      )}
                      <Pressable
                        onPress={() => handleRemoveRound(round.id)}
                        className="p-2"
                      >
                        <Trash2 size={18} color={colors.text.tertiary} />
                      </Pressable>
                    </View>
                  ))}
                </View>
              )}

              <Text style={{ color: colors.text.tertiary }} className="text-xs mt-3 text-center">
                Rounds can be verified by investors who participated
              </Text>
            </View>
          </Animated.View>
        </KeyboardAwareBody>
      </SafeAreaView>
    </View>
  );
}
